import * as Mutations from './mutations';
import router from '../../../router/';
import { Pagination, DefaultPagination } from './Pagination';
import { DefaultSorting, SortingDirection, toggleDirection } from './Sorting';

export const PageModule = {
    state: {
        pagination: new Pagination(),
        sorting: DefaultSorting,
        searchQuery: '',
        filters: null,
        scheduleFilter: null,
        rosterFilter: null,
        waitlistFilter: null,
        dropinFilter: null,
        attendanceFilter: null
    },

    mutations: {
        [Mutations.SET_PAGINATION] (state, payload) {
            state.pagination = payload;
        },

        [Mutations.REMOVE_PAGINATION] (state) {
            state.pagination = new Pagination();
        },

        [Mutations.REMOVE_SORTING] (state) {
            state.sorting.sortBy = null;
            state.sorting.direction = 'ASC';
        },

        [Mutations.GO_TO_PAGE] (state, pageNumber) {
            if (pageNumber <= DefaultPagination.firstPage) {
                state.pagination.pageNumber = DefaultPagination.firstPage;
            } else if (pageNumber >= state.pagination.totalPages - 1) {
                state.pagination.pageNumber = state.pagination.totalPages - 1;
            } else {
                state.pagination.pageNumber = pageNumber;
            }
        },

        [Mutations.SET_PAGE_SIZE] (state, pageSize) {
            state.pagination.pageSize = pageSize;
        },

        [Mutations.SET_SORT_BY] (state, sortBy) {
            if (!state.sorting.sortBy) {
                state.sorting.direction = SortingDirection.ASC;
            }
            else if (state.sorting.sortBy === sortBy) {
                state.sorting.direction = toggleDirection(state.sorting.direction);
            }
            else {
                state.sorting.direction = SortingDirection.DESC;
            }
            state.sorting.sortBy = sortBy;
        },

        [Mutations.SET_SEARCH] (state, query) {
            state.searchQuery = query;
        },

        [Mutations.REMOVE_SEARCH] (state) {
            state.searchQuery = '';
        },

        [Mutations.SET_FILTER] (state, filter) {
            if (state.filters === null) {
                state.filters = {};
            }
            state.filters = {
                ...state.filters,
                ...filter
            };
        },

        [Mutations.SET_SCHEDULE_FILTER] (state, filter) {
            state.scheduleFilter = filter;
        },

        [Mutations.SET_ROSTER_FILTER] (state, filter) {
            state.rosterFilter = filter;
        },

        [Mutations.SET_WAITLIST_FILTER] (state, filter) {
            state.waitlistFilter = filter;
        },

        [Mutations.SET_ATTENDANCE_FILTER] (state, filter) {
            state.attendanceFilter = filter;
        },

        [Mutations.SET_DROPIN_FILTER] (state, filter) {
            state.dropinFilter = filter;
        },

        [Mutations.REMOVE_FILTER] (state) {
            if (state.filters) {
                state.filters = null;
            }
        },

        [Mutations.REMOVE_ALL_FILTERS] (state) {
            state.scheduleFilter = null;
            state.rosterFilter = null;
            state.waitlistFilter = null;
            state.dropinFilter = null;
            state.attendanceFilter = null;
        },

        [Mutations.REMOVE_PAGES] (state) {
            if (state.pagination) {
                const callback = state.pagination.callback;
                state.pagination = {
                    callback
                };
            }
        }
    },

    actions: {
        configPagination(context, config) {
            context.commit(Mutations.SET_PAGINATION,
                Pagination.createPagination({
                    pageSize: config.pageSize,
                    totalPages: config.totalPages,
                    pageNumber: config.pageNumber,
                    totalElements: config.totalElements,
                    callback: config.callback,
                    numberOfElements: config.numberOfElements
                }));
        },

        async openNextPage(context, shouldScrollTop = true) {
            await context.dispatch('openPage', { pageNumber: context.getters.pagination.pageNumber + 1, shouldScrollTop });
        },

        async openPreviousPage(context, shouldScrollTop = true) {
            await context.dispatch('openPage', { pageNumber: context.getters.pagination.pageNumber - 1, shouldScrollTop });
        },

        async openPage(context, payload) {
            const pageNumber = (payload && payload.pageNumber !== undefined) ? payload.pageNumber : payload;
            if (Number.isInteger(pageNumber)) {
                context.commit(Mutations.GO_TO_PAGE, pageNumber);
                const shouldScrollTop = payload.shouldScrollTop;
                await context.dispatch('makeRequest', shouldScrollTop);
            }
        },

        async changePageSize(context, pageSize) {
            context.commit(Mutations.SET_PAGE_SIZE, pageSize);
            await context.dispatch('openPage', DefaultPagination.firstPage);
        },

        async sortBy(context, sortBy) {
            context.commit(Mutations.SET_SORT_BY, sortBy);
            await context.getters.pagination.callback();
        },

        async makeSearch(context, query) {
            context.commit(Mutations.SET_SEARCH, query);
            if ((query.length > 2 || query.length === 0) && router.app._route.name !== 'T-Shirt') {
                context.dispatch('resetPages');
                await context.getters.pagination.callback();
            }
        },

        async filterElements(context, filter) {
            if (filter) {
                context.dispatch('resetPages');
                context.commit(Mutations.SET_FILTER, filter);
            }
            if (router.app._route.name !== 'T-Shirt') {
                await context.getters.pagination.callback();
            }
        },

        async makeRequest(context, shouldScrollTop = true) {
            await context.getters.pagination.callback();
            if (shouldScrollTop) {
                window.scrollTo(0, 0);
            }
        },

        resetPagination(context) {
            context.commit(Mutations.REMOVE_PAGINATION);
        },

        resetSorting(context) {
            context.commit(Mutations.REMOVE_SORTING);
        },

        resetSearch(context) {
            context.commit(Mutations.REMOVE_SEARCH);
        },

        resetFilter(context) {
            context.commit(Mutations.REMOVE_FILTER);
        },

        resetPages(context) {
            context.commit(Mutations.REMOVE_PAGES);
        }
    },

    getters: {
        pagination: state => state.pagination,

        totalPages: state => state.pagination && state.pagination.totalPages || 0,

        pageSize: state => state.pagination && state.pagination.pageSize,

        pageNumber: state => state.pagination && state.pagination.pageNumber || 0,

        totalElements: state => state.pagination && state.pagination.totalElements || 0,

        numberOfElements: state => state.pagination && state.pagination.numberOfElements || 0,

        sortBy: state =>  state.sorting && state.sorting.sortBy,

        direction: state => state.sorting && state.sorting.direction,

        searchQuery: state => state.searchQuery,

        filters: state => state.filters,

        scheduleFilter: state => state.scheduleFilter,

        rosterFilter: state => state.rosterFilter,

        waitlistFilter: state => state.waitlistFilter,

        dropinFilter: state => state.dropinFilter,

        attendanceFilter: state => state.attendanceFilter,

        size: state => state.size
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/page/page.module.js